import { Component, h, Host, Prop, State, Watch } from "@stencil/core";
import { CalendarEventType } from "./interface";
import { getMonthNames } from "./utils";

/** static base class */
const baseClass = "ux-iws-event-calendar-item";

@Component({
    tag: "ux-iws-event-calendar-item",
    styleUrl: "event-calendar-item.scss",
})
export class EventCalendarItem {
    /** Event data used display the item */
    @Prop() data: CalendarEventType | null = null;
    /** Language used to localize texts  */
    @Prop() locale: string = "";
    /** Label shown before the hall property of an event */
    @Prop() hallLabel: string = "";
    /** Label shown before the booth property of an event */
    @Prop() boothLabel: string = "";

    @State() dateFormatter: Intl.DateTimeFormat;
    @State() monthNames: string[];

    @Watch("locale")
    setLocale() {
        const lng =
            this.locale.length > 1 ? this.locale.slice(0, 2) : undefined;
        this.dateFormatter = Intl.DateTimeFormat(lng, {
            month: "numeric",
            day: "numeric",
        });
        this.monthNames = getMonthNames(lng, "short");
    }

    componentWillLoad() {
        this.setLocale();
    }

    private renderEventDate = () => {
        const { data } = this;
        const startDate = new Date(data.startDate);
        const endDate = new Date(data.endDate);
        let dateLabel = data.startDate.split("-")[2];
        if (data.startDate !== data.endDate) {
            if (startDate.getMonth() !== endDate.getMonth()) {
                dateLabel = [
                    this.dateFormatter.format(startDate),
                    this.dateFormatter.format(endDate),
                ].join(" – ");
            } else {
                dateLabel = [
                    data.startDate.split("-")[2],
                    data.endDate.split("-")[2],
                ].join(" – ");
            }
        }
        return dateLabel;
    };

    private renderTags = () => {
        const { data } = this;
        return (
            <div class={`${baseClass}__tags`}>
                {data.keywords.map((keyword) => (
                    <ux-tag disabled key={keyword.value}>
                        {keyword.label}
                    </ux-tag>
                ))}
            </div>
        );
    };

    private renderLocation = () => {
        const { data } = this;
        return (
            <div
                class={`${baseClass}__location`}
                itemProp="location"
                itemScope
                itemType="https://schema.org/Place"
            >
                {data.organization.name && (
                    <div itemProp="name" itemScope>
                        {data.organization.name}
                    </div>
                )}
                {data.organization.address && (
                    <div
                        itemProp="addressCountry"
                        itemScope
                        itemType="https://schema.org/PostalAddress"
                    >
                        {data.organization.address}
                    </div>
                )}
            </div>
        );
    };

    private renderLinks = () => {
        const { data } = this;
        if (!Array.isArray(data.links)) {
            return null;
        }
        return (
            <div class={`${baseClass}__links`}>
                {data.links.map((link) => (
                    <a
                        class="ux-button"
                        key={link.href}
                        href={link.href}
                        target={link.target || "_blank"}
                    >
                        {link.label}
                    </a>
                ))}
            </div>
        );
    };

    private renderDate = () => {
        const { data, monthNames } = this;
        if (data === null) {
            return null;
        }
        const startDate = new Date(data.startDate);
        return (
            <div class={`${baseClass}__date-container`}>
                <time itemProp="startDate" dateTime={data.startDate}>
                    <div class={`${baseClass}__year`}>
                        {startDate.getFullYear()}
                    </div>
                    <div class={`${baseClass}__month`}>
                        {monthNames[startDate.getMonth()]}
                    </div>
                    <div class={`${baseClass}__date`}>
                        {this.renderEventDate()}
                    </div>
                </time>
            </div>
        );
    };

    private renderContent = () => {
        const { data } = this;
        if (data === null) {
            return null;
        }
        const hallText = `${data.hall}`;
        const boothText = `${data.booth}`;
        return (
            <div class={`${baseClass}__content-container`}>
                {(hallText || boothText) && (
                    <div class={`${baseClass}__booth`}>
                        {hallText !== "" && `${this.hallLabel} ${hallText}`}
                        {` `}
                        {boothText !== "" && `${this.boothLabel} ${boothText}`}
                    </div>
                )}
                <div class={`${baseClass}__title`} itemProp="name">
                    {data.title}
                </div>
                {this.renderTags()}
                {this.renderLocation()}
                {this.renderLinks()}
            </div>
        );
    };

    render() {
        return (
            <Host
                class={baseClass}
                itemScope
                itemType="https://schema.org/Event"
            >
                {this.renderDate()}
                {this.renderContent()}
                <slot />
            </Host>
        );
    }
}
